import React, { useState, useEffect, useCallback } from 'react';
import './CoreCocktails.css'; // Use dedicated Core Cocktails styles

const CoreCocktails = ({ onBack }) => {
  const [cocktails, setCocktails] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wrongCards, setWrongCards] = useState([]);
  const [studyMode, setStudyMode] = useState('normal'); // 'normal' or 'review'
  const [studyStats, setStudyStats] = useState({
    totalStudied: 0,
    correctAnswers: 0,
    sessionStartTime: Date.now(),
    streakCount: 0,
    bestStreak: 0
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const loadCoreCocktails = async () => {
    try {
      const response = await fetch('/core-cocktails.json');
      const data = await response.json();
      setCocktails(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading core cocktails:', error);
      setLoading(false);
    }
  };

  const getCurrentCards = () => {
    if (studyMode === 'review') {
      return wrongCards;
    }
    return cocktails;
  };

  const currentCards = getCurrentCards();
  const currentCard = currentCards[currentCardIndex];

  const nextCard = useCallback(() => {
    if (currentCardIndex < currentCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setIsFlipped(false);
      setShowDetails(false);
    } else {
      // Loop back to beginning
      setCurrentCardIndex(0);
      setIsFlipped(false);
      setShowDetails(false);
    }
  }, [currentCardIndex, currentCards.length]);

  const prevCard = useCallback(() => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setIsFlipped(false);
      setShowDetails(false);
    } else {
      // Loop to end
      setCurrentCardIndex(currentCards.length - 1);
      setIsFlipped(false);
      setShowDetails(false);
    }
  }, [currentCardIndex, currentCards.length]);

  useEffect(() => {
    loadCoreCocktails();
  }, []);

  // Keyboard event handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showDetails) {
        setShowDetails(false);
      } else if (event.key === ' ' && !showDetails) {
        event.preventDefault();
        setIsFlipped(!isFlipped);
      } else if (event.key === 'ArrowRight') {
        nextCard();
      } else if (event.key === 'ArrowLeft') {
        prevCard();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFlipped, showDetails, nextCard, prevCard]);

  const markAsWrong = () => {
    if (currentCard && studyMode === 'normal') {
      const exists = wrongCards.some(card => card.front === currentCard.front);
      if (!exists) {
        setWrongCards(prev => [...prev, currentCard]);
      }
    }
    
    // Reset streak
    setStudyStats(prev => ({
      ...prev,
      totalStudied: prev.totalStudied + 1,
      streakCount: 0
    }));
    
    nextCard();
  };

  const markAsCorrect = () => {
    if (studyMode === 'review' && currentCard) {
      setWrongCards(prev => prev.filter(card => card.front !== currentCard.front));
      if (wrongCards.length <= 1) {
        // Last card in review mode
        setStudyMode('normal');
        setCurrentCardIndex(0);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        if (currentCardIndex >= wrongCards.length - 1) {
          setCurrentCardIndex(0);
        }
      }
    } else {
      nextCard();
    }
    
    // Update stats and streak
    setStudyStats(prev => {
      const newStreak = prev.streakCount + 1;
      return {
        ...prev,
        totalStudied: prev.totalStudied + 1,
        correctAnswers: prev.correctAnswers + 1,
        streakCount: newStreak,
        bestStreak: Math.max(prev.bestStreak, newStreak)
      };
    });
  };

  const startReviewMode = () => {
    if (wrongCards.length > 0) {
      setStudyMode('review');
      setCurrentCardIndex(0);
      setIsFlipped(false);
      setShowDetails(false);
    }
  };

  const resetStudy = () => {
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setShowDetails(false);
    setWrongCards([]);
    setStudyMode('normal');
    setStudyStats({
      totalStudied: 0,
      correctAnswers: 0,
      sessionStartTime: Date.now(),
      streakCount: 0,
      bestStreak: 0
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return '#28a745';
      case 'Medium': return '#ffc107';
      case 'Hard': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getAccuracy = () => {
    if (studyStats.totalStudied === 0) return 0;
    return Math.round((studyStats.correctAnswers / studyStats.totalStudied) * 100);
  };

  if (loading) {
    return (
      <div className="core-cocktails-container">
        <div className="loading">Loading core cocktails...</div>
      </div>
    );
  }

  if (currentCards.length === 0) {
    return (
      <div className="core-cocktails-container">
        <button className="core-back-button" onClick={onBack}>
          ← Back to Menu
        </button>
        <div className="core-cocktails-header">
          <h2>🎯 Core Cocktails</h2>
          <p>No cocktails available for review</p>
        </div>
      </div>
    );
  }

  return (
    <div className="core-cocktails-container">
      <button className="core-back-button" onClick={onBack}>
        ← Back to Menu
      </button>
      
      <div className="core-cocktails-header">
        <h2>
          {studyMode === 'review' ? '🔄 Review Mode' : '🎯 Core Cocktails Drill'}
        </h2>
        <p>Master the essential cocktails you'll make every shift</p>
        
        {/* Enhanced Stats */}
        <div className="core-study-stats">
          <div className="core-stat-item">
            <span className="core-stat-label">Progress:</span>
            <span className="core-stat-value">{currentCardIndex + 1}/{currentCards.length}</span>
          </div>
          <div className="core-stat-item">
            <span className="core-stat-label">Session:</span>
            <span className="core-stat-value">{studyStats.correctAnswers}/{studyStats.totalStudied}</span>
          </div>
          <div className="core-stat-item">
            <span className="core-stat-label">Accuracy:</span>
            <span className="core-stat-value">{getAccuracy()}%</span>
          </div>
          <div className="core-stat-item">
            <span className="core-stat-label">Streak:</span>
            <span className="core-stat-value">{studyStats.streakCount} (Best: {studyStats.bestStreak})</span>
          </div>
          {wrongCards.length > 0 && studyMode === 'normal' && (
            <div className="core-stat-item">
              <span className="core-stat-label">To Review:</span>
              <span className="core-stat-value">{wrongCards.length}</span>
            </div>
          )}
        </div>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="core-success-message">
          🎉 Great job! You've mastered all the review cards!
        </div>
      )}

      <div className="core-cocktail-study">
        <div className={`core-cocktail-flashcard ${isFlipped ? 'flipped' : ''}`} onClick={() => setIsFlipped(!isFlipped)}>
          <div className="core-flashcard-inner">
            <div className="core-flashcard-front">
              <div className="core-card-content">
                <h3>{currentCard.front}</h3>
                <div className="core-flip-hint">
                  Click to reveal recipe
                </div>
              </div>
              <div 
                className="core-difficulty-badge"
                style={{ backgroundColor: getDifficultyColor(currentCard.difficulty) }}
              >
                {currentCard.difficulty}
              </div>
            </div>
            <div className="core-flashcard-back">
              <div className="core-card-content">
                <h3>Recipe:</h3>
                <p className="core-recipe">{currentCard.back}</p>
                
                <div className="core-recipe-details">
                  <div className="core-detail-row">
                    <strong>Method:</strong> <span>{currentCard.method}</span>
                  </div>
                  <div className="core-detail-row">
                    <strong>Glass:</strong> <span>{currentCard.glassware}</span>
                  </div>
                  <div className="core-detail-row">
                    <strong>Garnish:</strong> <span>{currentCard.garnish}</span>
                  </div>
                </div>
                
                <button 
                  className="core-details-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDetails(!showDetails);
                  }}
                >
                  {showDetails ? 'Hide Tips' : 'Show Tips'}
                </button>
              </div>
              <div className="core-flip-hint">
                Click to see name
              </div>
            </div>
          </div>
        </div>

        {/* Navigation and Action Buttons */}
        <div className="core-card-controls">
          <div className="core-action-buttons">
            <button className="core-wrong-button" onClick={markAsWrong}>
              ❌ Need Practice
            </button>
            <button className="core-correct-button" onClick={markAsCorrect}>
              ✅ Got It!
            </button>
          </div>
          
          <div className="core-nav-buttons">
            <button className="core-nav-button" onClick={prevCard}>
              ← Previous
            </button>
            <button className="core-nav-button" onClick={nextCard}>
              Next →
            </button>
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div className="core-keyboard-shortcuts">
          <span>⌨️ Shortcuts: Space (flip) • ← → (navigate) • Esc (close details)</span>
        </div>
      </div>

      {/* Review Section */}
      {wrongCards.length > 0 && studyMode === 'normal' && (
        <div className="core-review-section">
          <h3>🔄 Review Mode</h3>
          <p>You have {wrongCards.length} cocktails that need more practice</p>
          <button className="core-review-button" onClick={startReviewMode}>
            Review Missed Cocktails
          </button>
        </div>
      )}

      {/* Reset Button */}
      <div className="core-reset-section">
        <button className="core-reset-button" onClick={resetStudy}>
          🔄 Reset Session
        </button>
      </div>

      {/* Tips Modal */}
      {showDetails && (
        <div className="core-details-modal" onClick={() => setShowDetails(false)}>
          <div className="core-details-content" onClick={(e) => e.stopPropagation()}>
            <h3>Pro Tips for {currentCard.front}</h3>
            <p>{currentCard.tips}</p>
            <button onClick={() => setShowDetails(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoreCocktails; 